import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { getTimezoneLabel } from '../../utils/timezoneList';
import { formatDT, formatSpeed, formatDistance, speedUnitLabel, convertSpeed, formatTimeAgo } from '../../utils/timezone';
import { isPersonOccupant, peopleDeepLinkPath } from '../../utils/occupancy';
import { OccupancyPill } from '../fleet/OccupancyPill';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { deviceDisplayName } from '../../utils/deviceName';
import { getIoValueColorClass as getValueColorClass } from '../../utils/ioValueColors';
import { computeDisplayValue } from '../../utils/ioMappingValue';
import { beaconBatteryInfo } from './DeviceListPanel';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';
import {
  X,
  RefreshCw,
  Copy,
  Loader2,
  Compass,
  Gauge,
  Activity,
  Send,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Bluetooth,
  UserRound,
  Truck,
  Container,
  Users,
  IdCard,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────
// GPRS active = device is not offline (single source of truth from backend)
const isGPRSActive = (device) => device.status !== 'offline';

// Explicit GPS fix status (Digital Matter FID 0) — mirrors DeviceListPanel logic.
const getGpsFixState = (device) => {
  const fix = device.last_gps_fix;
  if (!fix || typeof fix !== 'object') return null;
  if (!fix.fix_valid) return { state: 'invalid', fix };
  const ageMs = fix.gps_timestamp ? Math.max(0, Date.now() - new Date(fix.gps_timestamp).getTime()) : null;
  const threshold = (device.offline_threshold_secs || 600) * 1000;
  if (fix.old_fix || ageMs == null || ageMs >= threshold) return { state: 'last_known', fix, ageMs };
  return { state: 'fresh', fix, ageMs };
};

// GPS active = has fresh GPS data within offline threshold AND satellites > 0
const isGPSActive = (device) => {
  if (!isGPRSActive(device)) return false;
  const fixState = getGpsFixState(device);
  if (fixState) return fixState.state === 'fresh';
  const gpsTime = device.last_gps_time || device.last_position?.timestamp;
  if (!gpsTime) return false;
  const threshold = (device.offline_threshold_secs || 600) * 1000;
  if ((Date.now() - new Date(gpsTime).getTime()) >= threshold) return false;
  const sats = device.last_satellites ?? device.last_position?.satellites ?? 0;
  return sats > 0;
};

// Format an absolute timestamp using the user's configured date/time format
// (Settings > General) via the shared timezone util — never hardcode the format.
// `timezone` is the device's effective timezone (formatDT falls back to the
// user's timezone when it is not provided).
const formatTimestamp = (ts, timezone) => {
  if (!ts) return 'No data';
  return formatDT(ts, { timezone, seconds: true });
};

const getStatusDuration = (device) => {
  // Use status_since (when the current status started) if available,
  // otherwise fall back to last_seen
  const ts = device.status_since || device.last_seen || device.last_position?.server_timestamp;
  if (!ts) return 'Unknown';
  const diffMs = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
};

const getStatusLabel = (device) => {
  const dur = getStatusDuration(device);
  const labels = { moving: 'Moving', idle: 'Idling', stopped: 'Stopped', offline: 'Offline' };
  return `${labels[device.status] || device.status || 'Unknown'} for ${dur}`;
};

const getStatusColor = (status) => {
  const map = {
    moving: 'bg-blue-600 text-white',
    idle: 'bg-yellow-600 text-white',
    stopped: 'bg-red-600 text-white',
    offline: 'bg-gray-500 text-white',
  };
  return map[status] || 'bg-slate-200 text-slate-600';
};

const DRIVER_SOURCE_LABELS = {
  ble_algorithm: 'BLE Auto',
  driver_confirmation: 'Driver accepted',
  manager_override: 'Manager override',
  manager_directory_override: 'Manager assignment',
  ibutton_rfid: 'RFID / iButton',
  manual_assignment: 'Assigned driver (manual)',
};

const driverSourceLabel = (session) => {
  if (!session) return 'Confirmed';
  if (session.resolution_reason === 'last_remaining_after_rejections') return 'Auto resolved';
  return DRIVER_SOURCE_LABELS[session.decision_source] || 'Confirmed';
};

const isAssetBeaconDevice = (device) => device?.device_type === 'beacon' && (device?.beacon_purpose || 'asset') === 'asset';

const formatBeaconBattery = (device) => {
  const st = device?.beacon_live_state;
  const pct = device?.beacon_battery_percent ?? st?.last_battery_percent;
  const mv = device?.beacon_battery_voltage_mv ?? st?.last_battery_mv;
  const low = device?.beacon_low_battery ?? st?.low_battery;
  if (pct == null && mv == null && low == null) return 'Not reported';
  const parts = [];
  if (pct != null) parts.push(`~${pct}%`);
  if (mv != null) parts.push(`${mv} mV`);
  let out = parts.join(' · ') || 'Voltage unknown';
  if (low === true) out += ' · Low'; else if (low === false) out += ' · OK';
  return out;
};

const headingToCompass = (deg) => {
  if (deg == null) return 'N/A';
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16] + ` (${Math.round(deg)}°)`;
};

// ── GPS Satellite Icon (matches device list panel) ─────────
const SatelliteIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.118,6.606 L16.615,10.698 L18.77,12.852 C19.375,13.458 19.903,13.986 20.248,14.493 C20.633,15.061 20.831,15.661 20.719,16.387 C20.709,16.455 20.688,16.557 20.672,16.623 C20.509,17.288 20.182,17.753 19.678,18.116 C19.247,18.427 18.682,18.661 18.078,18.911 L18.015,18.937 C16.719,19.474 15.331,19.75 13.928,19.75 C12.526,19.75 11.137,19.474 9.842,18.937 C9.238,18.687 8.66,18.382 8.114,18.028 L9.689,21.705 C9.789,21.936 9.765,22.202 9.626,22.413 C9.487,22.623 9.252,22.75 9,22.75 L3,22.75 C2.748,22.75 2.513,22.623 2.374,22.413 C2.235,22.202 2.211,21.936 2.311,21.705 L5.131,15.124 C4.708,14.509 4.35,13.851 4.063,13.158 C3.526,11.863 3.25,10.474 3.25,9.072 C3.25,7.669 3.526,6.281 4.063,4.985 L4.089,4.922 C4.339,4.318 4.573,3.753 4.884,3.322 C5.247,2.818 5.713,2.491 6.377,2.328 C6.443,2.312 6.545,2.291 6.613,2.281 C7.339,2.169 7.939,2.367 8.507,2.752 C9.014,3.097 9.542,3.625 10.148,4.23 L10.195,4.278 L12.322,6.405 L16.489,5.123 C16.336,4.78 16.25,4.4 16.25,4 C16.25,2.481 17.481,1.25 19,1.25 C20.519,1.25 21.75,2.481 21.75,4 C21.75,5.519 20.519,6.75 19,6.75 C18.691,6.75 18.395,6.699 18.118,6.606 Z" />
  </svg>
);

// ── IO Value computation moved to shared utils/ioMappingValue.js ─
// (used by this panel AND the Fleet vehicle cards — keep them identical)

// Value colour rendering — shared palette + helper (utils/ioValueColors) so it
// stays in sync with the IO Mapping picker and the Public Tracking page.

// ── Command button colours (exact match from DeviceManagement) ──
const CMD_BG = {
  blue: 'bg-blue-600 hover:bg-blue-700',
  red: 'bg-red-600 hover:bg-red-700',
  green: 'bg-green-600 hover:bg-green-700',
  yellow: 'bg-yellow-500 hover:bg-yellow-600',
  orange: 'bg-orange-500 hover:bg-orange-600',
  purple: 'bg-purple-600 hover:bg-purple-700',
  gray: 'bg-gray-500 hover:bg-gray-600',
  teal: 'bg-teal-600 hover:bg-teal-700',
  pink: 'bg-pink-600 hover:bg-pink-700',
  indigo: 'bg-indigo-600 hover:bg-indigo-700',
  cyan: 'bg-cyan-600 hover:bg-cyan-700',
  lime: 'bg-lime-600 hover:bg-lime-700',
  rose: 'bg-rose-600 hover:bg-rose-700',
};

// ── Info Row ──────────────────────────────────────────────
const InfoRow = ({ label, value, mono, className, testId, wrap = false }) => (
  <div className={`flex justify-between ${wrap ? 'items-start gap-2' : 'items-center'} py-1 min-h-[24px]`}>
    <span className="text-xs text-muted-foreground flex-shrink-0">{label}</span>
    <span className={`text-xs font-medium text-right ${wrap ? 'break-all whitespace-normal min-w-0' : 'max-w-[55%] truncate'} ${mono ? 'font-mono' : ''} ${className || ''}`} data-testid={testId}>
      {value ?? 'N/A'}
    </span>
  </div>
);

// ── Collapsible Section ───────────────────────────────────
// Live Occupancy list (current + last bound identities, mirrors the Fleet card).
// Desktop: own section under the Overview. Mobile webview: shown inside the Occupants (driver) card.
const LiveOccupancySection = ({ bindings, tid, isSubpageEnabled, navigate, effectiveTz, offline = false, mobile = false, showEmpty = false }) => {
  const ROWS = [
    ['driver', 'Driver', IdCard, 'live_tracking_driver'],
    ['staff', 'Staff', UserRound, 'live_tracking_staff'],
    ['passenger', 'Passengers', Users, 'live_tracking_passengers'],
    ['trailer', 'Trailer', Truck, 'live_tracking_assets'],
    ['equipment', 'Equipment', Container, 'live_tracking_assets'],
  ];
  const built = ROWS.map(([type, label, Icon, featureKey]) => {
    if (featureKey && !isSubpageEnabled(featureKey)) return null;
    const b = (bindings || {})[type];
    if (!b) return null;
    // Carry each occupant's ACTUAL person_type — a driver-eligible Staff acting as the
    // driver has person_type "staff", so the People deep-link must open the Staff subpage,
    // not Drivers. Fall back to the row type for assets / missing person_type.
    const currentArr = Array.isArray(b.current) ? b.current : (b.current ? [b.current] : []);
    const currentPeople = currentArr
      .map((c) => ({ name: c.person_name || c.beacon_name || c.entity_name, pid: c.person_id, ptype: c.person_type || type, source: c.binding_source || c.decision_source || c.credential_source, lastSeen: c.last_seen_at }))
      .filter((p) => p.name);
    const names = currentPeople.map((p) => p.name);
    // "Last" can be a list (all occupants from the last trip) or a single object (driver).
    const lastArr = Array.isArray(b.last) ? b.last : (b.last ? [b.last] : []);
    const lastPeople = lastArr
      .map((c) => ({ name: c.person_name || c.beacon_name || c.entity_name, pid: c.person_id, ptype: c.person_type || type, source: c.binding_source || c.decision_source || c.credential_source }))
      .filter((p) => p.name);
    const lastNames = lastPeople.map((p) => p.name);
    const lastEndedAt = lastArr.length ? (lastArr[0].ended_at || null) : null;
    if (names.length === 0 && lastNames.length === 0) return null;
    return { type, label, Icon, names, lastNames, lastEndedAt, currentPeople, lastPeople };
  }).filter(Boolean);
  if (built.length === 0) {
    if (showEmpty) {
      return (
        <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid={tid('telemetry-bindings-empty')}>
          No driver or occupants detected on this vehicle.
        </div>
      );
    }
    return null;
  }
  return (
    <div className={mobile ? 'px-3 py-2' : 'px-3 py-2 border-b border-border'} data-testid={tid('telemetry-bindings')}>
      <div className="flex items-center gap-2 text-sm font-semibold mb-1">
        <Users className="w-4 h-4 text-[#2563eb]" /> Live Occupancy
      </div>
      {built.map(({ type, label, Icon, lastEndedAt, currentPeople, lastPeople }) => (
        <div key={type} className="flex justify-between items-start py-0.5 gap-2" data-testid={tid(`binding-${type}`)}>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 shrink-0">
            <Icon className="w-3 h-3" /> {label}
          </span>
          <div className="text-right">
            {currentPeople.length > 0 ? (
              <div className="flex flex-wrap justify-end items-center gap-1">
                {currentPeople.map((p, i) => (
                  <OccupancyPill
                    key={i}
                    rowType={type}
                    name={p.name}
                    personType={p.ptype}
                    source={p.source}
                    stale={offline && (type === 'trailer' || type === 'equipment')}
                    staleNote={offline && (type === 'trailer' || type === 'equipment') ? `Device offline — attachment last confirmed ${p.lastSeen ? formatTimeAgo(p.lastSeen) : 'before the device went offline'}` : undefined}
                    onClick={isPersonOccupant(p.ptype) ? () => navigate(peopleDeepLinkPath(p.ptype, p.pid, p.name)) : undefined}
                    testId={tid(`binding-${type}-current`)}
                  />
                ))}
                {offline && (type === 'trailer' || type === 'equipment') && currentPeople[0]?.lastSeen && (
                  <span className="text-[9px] text-muted-foreground/70 ml-0.5" data-testid={tid(`binding-${type}-stale-note`)}>· last confirmed {formatTimeAgo(currentPeople[0].lastSeen)}</span>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap justify-end items-center gap-1" data-testid={tid(`binding-${type}-last`)}>
                <span className="text-[10px] text-muted-foreground">Last:</span>
                {lastPeople.map((p, i) => (
                  <OccupancyPill
                    key={i}
                    rowType={type}
                    name={p.name}
                    personType={p.ptype}
                    source={p.source}
                    last
                    onClick={isPersonOccupant(p.ptype) ? () => navigate(peopleDeepLinkPath(p.ptype, p.pid, p.name)) : undefined}
                    testId={tid(`binding-${type}-last-link-${i}`)}
                  />
                ))}
                {lastEndedAt && (
                  <span className="text-[9px] text-muted-foreground/70 ml-0.5" title={formatDT(lastEndedAt, { timezone: effectiveTz })}>· {formatTimeAgo(lastEndedAt)}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, testId, defaultOpen = true, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border" data-testid={testId}>
      <button
        className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen((p) => !p)}
      >
        <h4 className="text-sm font-semibold flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#2563eb]" />}
          {title}
        </h4>
        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
      {isOpen && <div className="px-3 pb-2">{children}</div>}
    </div>
  );
};

// On mobile single-section view, render content directly without the collapsible wrapper
// because the modal header already shows the section title.
const SectionContainer = ({ mobile, title, icon, testId, defaultOpen = true, children, mobileClassName = '' }) => {
  if (mobile) {
    return (
      <div className={`px-3 py-2 ${mobileClassName}`} data-testid={testId}>{children}</div>
    );
  }
  return (
    <CollapsibleSection title={title} icon={icon} testId={testId} defaultOpen={defaultOpen}>{children}</CollapsibleSection>
  );
};

// ── Speed Limit Sign SVG ──────────────────────────────────
const SpeedLimitSign = ({ limit, size = 28, testIdPrefix = '' }) => (
  <svg viewBox="0 0 455.111 455.111" width={size} height={size} data-testid={`${testIdPrefix}speed-limit-sign`}>
    <path fill="#E24C4B" d="M227.556,0C102.4,0,0,102.4,0,227.556s102.4,227.556,227.556,227.556s227.556-102.4,227.556-227.556S352.711,0,227.556,0z" />
    <path fill="#FFFFFF" d="M227.556,412.444c-102.4,0-184.889-82.489-184.889-184.889S125.156,42.667,227.556,42.667s184.889,82.489,184.889,184.889S329.956,412.444,227.556,412.444z" />
    <text x="227.556" y="245" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize={limit >= 100 ? '160' : '180'} fill="#1a1a1a">{limit}</text>
  </svg>
);

// ── Main Component ────────────────────────────────────────
export const TelemetryPanel = ({ device, onClose, api, userTimezone, roadSpeedLimit, roadSpeedSource, roadName, refreshKey, activeEvents = [], onViewEventOnMap, className = '', testId = 'telemetry-panel', mobileSection = null, testIdPrefix = '' }) => {
  const { isSubpageEnabled, isFeatureEnabled } = useFeatureFlags();
  const [address, setAddress] = useState(null);
  const [addressSource, setAddressSource] = useState(null); // 'google', 'osm', or 'nominatim'
  const [countryCode, setCountryCode] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [sendConfirmCmd, setSendConfirmCmd] = useState(null);
  const isMobileWebview = useIsMobileOrWebview();
  const [sendingCommand, setSendingCommand] = useState(false);

  // Reverse geocode the actual device GPS coordinate. Do not snap here: the
  // address and country flag must represent the real telemetry point.
  const fetchAddress = useCallback(async () => {
    if (!device?.last_latitude || !device?.last_longitude) {
      setAddress(null);
      setAddressSource(null);
      setCountryCode(null);
      return;
    }
    setAddressLoading(true);
    const lat = device.last_latitude;
    const lng = device.last_longitude;

    // Use Google Geocoder if: feature flag ON + user privilege ON + Google Maps loaded
    // Otherwise: use self-hosted Nominatim with clean address format
    const useGoogle = isFeatureEnabled('google_reverse_geocoding') && window.google?.maps?.Geocoder;
    
    if (useGoogle) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setAddress(results[0].formatted_address);
          setAddressSource('google');
          const cc = results[0].address_components?.find(c => c.types?.includes('country'));
          setCountryCode(cc?.short_name || null);
        } else {
          setAddress('Address not found');
          setAddressSource(null);
        }
        setAddressLoading(false);
      });
    } else {
      // Nominatim reverse-geocode via the backend so the frontend uses the SAME runtime
      // Nominatim URL that Admin Panel > Maps configures (previously we hit Nominatim
      // directly using a build-time env var that was empty in production → "Geocoding
      // not configured"). Backend endpoint reads global_settings.nominatim_config first,
      // then falls back to NOMINATIM_URL env var, then public OSM.
      try {
        const resp = await api.get(`/geocode/reverse?lat=${lat}&lng=${lng}`);
        const data = resp?.data || {};
        setAddress(data.formatted_address || 'Address not found');
        setAddressSource(data.source || 'nominatim');
        setCountryCode(data.country_code || null);
      } catch (err) {
        // 503 => Nominatim URL not configured at all. Other errors => transient failure.
        const status = err?.response?.status;
        if (status === 503) {
          setAddress('Geocoding not configured');
        } else {
          setAddress('Geocoding failed');
        }
        setAddressSource(null);
      } finally {
        setAddressLoading(false);
      }
    }
  }, [device?.last_latitude, device?.last_longitude, isFeatureEnabled, api]);

  // Trigger geocode when opening a new device OR when refreshKey changes (re-click same device)
  useEffect(() => {
    fetchAddress();
  }, [fetchAddress, device?.id, refreshKey]);

  // Send command — with dialog confirmation matching DeviceManagement style
  const executeSendCommand = async (cmd) => {
    setSendingCommand(true);
    try {
      // Use linked device IMEI if command targets a linked device, otherwise primary's IMEI
      const targetImei = cmd.target_device_imei || device.imei;
      if (cmd.command_mode === 'sequence' && cmd.command_sequence?.length > 0) {
        await api.post('/parsing/commands/send-sequence', {
          device_imei: targetImei,
          steps: cmd.command_sequence.map(s => ({
            command: s.command_text,
            delay_ms: s.delay_ms || 0,
          })),
          button_label: cmd.button_label,
        });
        toast.success(`Sequence "${cmd.button_label}" sent (${cmd.command_sequence.length} steps)`);
      } else {
        await api.post('/parsing/commands/send', {
          device_imei: targetImei,
          command: cmd.command_text,
          command_type: 'custom',
        });
        toast.success(`Command "${cmd.button_label}" sent`);
      }
      setSendConfirmCmd(null);
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSendingCommand(false);
    }
  };

  const handleSendCommand = (cmd) => {
    if (cmd.requires_confirmation) {
      setSendConfirmCmd(cmd);
    } else {
      executeSendCommand(cmd);
    }
  };

  // IO Mappings to display
  const isIgnitionOff = useMemo(() => {
    if (!device || device.status === 'offline') return true;
    if (device.status === 'moving' || device.status === 'idle') return false;
    // Check IO elements for ignition state
    const io = device.last_io_elements;
    if (io) {
      const ignMap = (device.io_mappings || []).find(m => m.io_type?.toLowerCase() === 'ignition');
      if (ignMap && io[ignMap.io_id] !== undefined) return Number(io[ignMap.io_id]) <= 0;
      if (io['239'] !== undefined) return Number(io['239']) <= 0;
      if (io['ignition'] !== undefined) return Number(io['ignition']) <= 0;
      if (io['din1'] !== undefined) return Number(io['din1']) <= 0;
    }
    return device.status === 'stopped';
  }, [device]);

  const telemetryMappings = useMemo(() => {
    return (device.io_mappings || []).filter((m) => {
      if (m.show_telemetry === false) return false;
      if (isIgnitionOff && m.ignore_ignition_off) return false;
      return true;
    });
  }, [device.io_mappings, isIgnitionOff]);

  // Flatten nested IO elements with dot notation (e.g. DM: analog.internal_battery_mv)
  const flatIOElements = useMemo(() => {
    const raw = device.last_io_elements;
    if (!raw) return {};
    const flat = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        for (const [ck, cv] of Object.entries(v)) {
          flat[`${k}.${ck}`] = cv;
        }
      } else {
        flat[k] = v;
      }
    }
    return flat;
  }, [device.last_io_elements]);

  // Commands to display
  const telemetryCommands = useMemo(() => {
    return (device.custom_commands || []).filter((c) => c.show_telemetry !== false);
  }, [device.custom_commands]);

  // Events from Event Engine — active events passed as prop + fetch recent events from API
  const [recentDeviceEvents, setRecentDeviceEvents] = useState([]);
  useEffect(() => {
    if (!device?.id || !api) return;
    const fetchDeviceEvents = async () => {
      try {
        const since = new Date(Date.now() - 48 * 3600000).toISOString();
        const res = await api.get(`/event-occurrences?device_id=${device.id}&start_date=${since}&limit=20`);
        setRecentDeviceEvents(res.data?.items || []);
      } catch { setRecentDeviceEvents([]); }
    };
    fetchDeviceEvents();
    const interval = setInterval(fetchDeviceEvents, 15000);
    return () => clearInterval(interval);
  }, [device?.id, api]);

  // Phase 3B — Vehicle Beacon Bindings (trailer/equipment/driver/passenger/staff).
  const navigate = useNavigate();
  const [bindings, setBindings] = useState(null);
  useEffect(() => {
    if (!device?.id || !api || isAssetBeaconDevice(device)) { setBindings(null); return; }
    let alive = true;
    const fetchBindings = async () => {
      try {
        const res = await api.get(`/fleet/vehicles/${device.id}/bindings`);
        if (alive) setBindings(res.data?.bindings || null);
      } catch { if (alive) setBindings(null); }
    };
    fetchBindings();
    const interval = setInterval(fetchBindings, 5000);
    return () => { alive = false; clearInterval(interval); };
  }, [device, api, device?.id, device?.last_server_timestamp, device?.last_seen, refreshKey]);

  // Merge: active events from WebSocket (real-time) + recent from API (historical),
  // then enforce the panel's contract: "Last 20 events up to 48 hours".
  // Active events older than 48h are intentionally excluded HERE only — they
  // remain fully visible on the Analytics → Events page. Without this cap the
  // list would render every active occurrence (was showing 128+), contradicting
  // the "Last 20 events up to 48 hours" label.
  const deviceEvents = useMemo(() => {
    const activeIds = new Set((activeEvents || []).map(e => e.id));
    const merged = [...(activeEvents || [])];
    for (const evt of recentDeviceEvents) {
      if (!activeIds.has(evt.id)) merged.push(evt);
    }
    const cutoffMs = Date.now() - 48 * 3600000;
    return merged
      .filter((e) => {
        if (!e.triggered_at) return false;
        const t = new Date(e.triggered_at).getTime();
        return Number.isFinite(t) && t >= cutoffMs;
      })
      .sort((a, b) => new Date(b.triggered_at).getTime() - new Date(a.triggered_at).getTime())
      .slice(0, 20);
  }, [activeEvents, recentDeviceEvents]);

  const gprsActive = isGPRSActive(device);
  const gpsActive = isGPSActive(device);
  const hasPosition = device.last_latitude && device.last_longitude;
  const coordsStr = hasPosition ? `${device.last_latitude.toFixed(6)}, ${device.last_longitude.toFixed(6)}` : null;
  const isAssetBeacon = isAssetBeaconDevice(device);
  const beaconFresh = isAssetBeacon && device.status !== 'offline';
  const activeDriverSession = device.active_driver_session
    || device.current_driver_session
    || bindings?.driver?.current
    || null;

  // Fresh GPS: online AND last_gps_time within the device's offline threshold
  const gpsFresh = (() => {
    if (device.status === 'offline') return false;
    const gpsTime = device.last_gps_time || device.last_position?.timestamp;
    if (!gpsTime) return false;
    const threshold = (device.offline_threshold_secs || 600) * 1000;
    return (Date.now() - new Date(gpsTime).getTime()) < threshold;
  })();

  // Timezone: use effective_timezone from backend, or user's timezone
  const effectiveTz = device.effective_timezone || userTimezone;
  const tid = (id) => `${testIdPrefix}${id}`;
  // Vehicle photo — only loaded when the panel is open for a device that HAS one
  // (single blob request; nothing is bulk-loaded on the Live Tracking page itself).
  const [vehiclePhotoUrl, setVehiclePhotoUrl] = useState(null);
  useEffect(() => {
    const u = device?.vehicle_photo?.url;
    if (!u || !api || mobileSection) { setVehiclePhotoUrl(null); return; }
    let cancelled = false; let obj = null;
    api.get(u.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((r) => { if (!cancelled) { obj = URL.createObjectURL(r.data); setVehiclePhotoUrl(obj); } })
      .catch(() => {});
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [api, device?.vehicle_photo?.url, mobileSection]);

  const tzMatchesUser = effectiveTz === userTimezone;
  const isDifferentTz = !tzMatchesUser;
  const tzDisplayLabel = getTimezoneLabel(effectiveTz);
  const panelTitle = mobileSection
    ? {
        overview: 'Telemetry Overview',
        driver: 'Occupants',
        data: 'Telemetry Data',
        commands: 'Commands',
        events: 'Events',
      }[mobileSection] || deviceDisplayName(device)
    : deviceDisplayName(device);

  return (
    <div
      className={`${className || 'w-80'} ${mobileSection ? 'telemetry-mobile' : 'flex-shrink-0'} bg-card rounded-lg border border-border overflow-hidden flex flex-col`}
      data-testid={testId}
    >
      {/* Header — matches asset name style */}
      <div className={`${mobileSection ? 'px-4 py-3' : 'px-3 py-2'} flex items-center justify-between border-b border-border bg-muted/30`}>
        <h3 className={`${mobileSection ? 'text-[22px] leading-tight' : 'text-base'} font-bold truncate`} data-testid={tid('telemetry-device-name')}>
          {panelTitle}
        </h3>
        {!mobileSection && (
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClose} data-testid={tid('telemetry-close-button')} aria-label="Close telemetry panel">
            <X className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* ── TOP SECTION ── */}
        {(!mobileSection || mobileSection === 'overview') && (
        <div className="px-3 py-2 space-y-0.5 border-b border-border" data-testid={tid('telemetry-top-section')}>
          {vehiclePhotoUrl && (
            <div className="flex justify-center pb-1.5">
              <img src={vehiclePhotoUrl} alt="Vehicle" className="w-28 h-28 rounded-lg object-cover border" data-testid={tid('telemetry-vehicle-photo')} />
            </div>
          )}
          {isAssetBeacon && (
            <div className="mb-2 rounded-md border border-teal-200 bg-teal-50 px-2 py-2 text-xs dark:border-teal-900 dark:bg-teal-950/30" data-testid={tid('asset-beacon-observed-banner')}>
              <div className="flex items-center gap-1.5 font-semibold text-teal-800 dark:text-teal-200">
                <Bluetooth className="w-3.5 h-3.5" /> Observed Asset Beacon
              </div>
              <p className="mt-1 text-[11px] leading-snug text-teal-700 dark:text-teal-300">
                Location is derived from the GPS tracker that last detected this beacon.
              </p>
            </div>
          )}

          <InfoRow label={isAssetBeacon ? 'Beacon ID' : 'IMEI'} value={device.imei} mono wrap={isAssetBeacon} testId={tid('telemetry-device-id')} />
          <div className="py-1 min-h-[40px]">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Last Data</span>
              <span className="text-xs font-medium text-right truncate max-w-[60%]">
                {formatTimestamp(device.last_seen || device.last_position?.server_timestamp, effectiveTz)}
              </span>
            </div>
            <div className="flex justify-end mt-0.5">
              <span className={`text-[9px] font-medium ${tzMatchesUser ? 'text-blue-400' : 'text-amber-500'}`}>{tzDisplayLabel}</span>
            </div>
          </div>
          <div className="py-1 min-h-[40px]">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Last GPS</span>
              <span className="text-xs font-medium text-right truncate max-w-[60%]">
                {formatTimestamp(device.last_gps_time, effectiveTz)}
              </span>
            </div>
            <div className="flex justify-end mt-0.5">
              <span className={`text-[9px] font-medium ${tzMatchesUser ? 'text-blue-400' : 'text-amber-500'}`}>{tzDisplayLabel}</span>
            </div>
          </div>

          {/* Status */}
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground">Status</span>
            <Badge className={`text-[10px] px-1.5 py-0 ${isAssetBeacon ? (beaconFresh ? 'bg-teal-600 text-white' : 'bg-gray-500 text-white') : getStatusColor(device.status)}`} data-testid={tid('telemetry-status')}>
              {isAssetBeacon
                ? (beaconFresh ? `Observed · ${device.beacon_confidence || device.beacon_live_state?.confidence || 'low'}` : 'Not Detected')
                : getStatusLabel(device)}
            </Badge>
          </div>

          {/* Asset details — shown in both desktop and mobile Overview */}
          {(device.asset_name || device.asset_make || device.asset_model || device.registration) ? (
            <>
              {device.asset_name ? <InfoRow label="Asset" value={device.asset_name} wrap testId={tid('telemetry-asset-name')} /> : null}
              {(device.asset_make || device.asset_model) ? (
                <InfoRow
                  label="Make & Model"
                  value={[[device.asset_make, device.asset_model].filter(Boolean).join(' '), device.asset_year].filter(Boolean).join(' · ')}
                  wrap
                  testId={tid('telemetry-make-model')}
                />
              ) : null}
              {device.registration ? <InfoRow label="Rego" value={device.registration} testId={tid('telemetry-rego')} /> : null}
            </>
          ) : null}

          {isAssetBeacon && (
            <>
              <InfoRow label="Last Seen By" value={device.beacon_last_seen_by || device.beacon_live_state?.last_receiver_name || 'N/A'} />
              <InfoRow label="RSSI" value={device.beacon_last_rssi_dbm != null ? `${device.beacon_last_rssi_dbm} dBm` : 'Not reported'} mono />
              <InfoRow label="Battery" value={(() => {
                const b = beaconBatteryInfo(device, !beaconFresh);
                return (
                  <span className="inline-flex items-center gap-1" data-testid={tid('beacon-battery-row')}>
                    <b.Icon className={`w-3.5 h-3.5 flex-shrink-0 ${b.cls}`} />
                    {formatBeaconBattery(device)}
                  </span>
                );
              })()} />
            </>
          )}

          {/* Signal Status — uses same satellite icon as device list */}
          <div className="flex justify-between items-center py-1">
            <span className="text-xs text-muted-foreground">Signal</span>
            <div className="flex items-center gap-2">
              {!isAssetBeacon && (
              <span className={`text-[10px] font-medium flex items-center gap-0.5 ${gprsActive ? 'text-green-600' : 'text-muted-foreground'}`}>
                {gprsActive ? (
                  <svg viewBox="0 0 24 24" className="w-3 h-3"><path fillRule="evenodd" clipRule="evenodd" fill="currentColor" d="M18.75 4.25C18.96 4.25 19.18 4.25 19.35 4.26C19.53 4.27 19.72 4.3 19.92 4.38C20.35 4.56 20.69 4.9 20.87 5.33C20.95 5.53 20.98 5.72 20.99 5.9C21 6.0699 21 6.25978 21 6.47962V6.48V17.52V17.5204C21 17.7402 21 17.9301 20.99 18.1C20.98 18.28 20.95 18.47 20.87 18.67C20.69 19.1 20.35 19.44 19.92 19.62C19.72 19.7 19.53 19.73 19.35 19.74C19.18 19.75 18.94 19.75 18.73 19.75C18.52 19.75 18.32 19.75 18.15 19.74C17.97 19.73 17.78 19.7 17.58 19.62C17.15 19.44 16.81 19.1 16.63 18.67C16.55 18.47 16.52 18.28 16.51 18.1C16.5 17.9301 16.5 17.7402 16.5 17.5204V17.52V6.48V6.47962C16.5 6.25978 16.5 6.0699 16.51 5.9C16.52 5.72 16.55 5.53 16.63 5.33C16.81 4.9 17.15 4.56 17.58 4.38C17.78 4.3 17.97 4.27 18.15 4.26C18.32 4.25 18.53 4.25 18.75 4.25ZM12.25 7.25C12.46 7.25 12.68 7.25 12.85 7.26C13.03 7.27 13.22 7.3 13.42 7.38C13.85 7.56 14.19 7.9 14.37 8.33C14.45 8.53 14.48 8.72 14.49 8.9C14.5 9.0699 14.5 9.25978 14.5 9.47962V9.48V17.52V17.5204C14.5 17.7402 14.5 17.9301 14.49 18.1C14.48 18.28 14.45 18.47 14.37 18.67C14.19 19.1 13.85 19.44 13.42 19.62C13.22 19.7 13.03 19.73 12.85 19.74C12.68 19.75 12.49 19.75 12.27 19.75C12.06 19.75 11.81 19.75 11.65 19.74C11.47 19.73 11.28 19.7 11.08 19.62C10.65 19.44 10.31 19.1 10.13 18.67C10.05 18.47 10.02 18.28 10.01 18.1C10 17.9301 10 17.7402 10 17.5204V17.52V9.48V9.47962C10 9.25978 10 9.0699 10.01 8.9C10.02 8.72 10.05 8.53 10.13 8.33C10.31 7.9 10.65 7.56 11.08 7.38C11.28 7.3 11.47 7.27 11.65 7.26C11.82 7.25 12.03 7.25 12.25 7.25ZM6.35001 11.26C6.18001 11.25 5.98 11.25 5.77 11.25C5.56 11.25 5.31999 11.25 5.14999 11.26C4.96999 11.27 4.78 11.3 4.58 11.38C4.15 11.56 3.81 11.9 3.63 12.33C3.55 12.53 3.51999 12.72 3.50999 12.9C3.5 13.0699 3.5 13.2598 3.5 13.4796V13.48V17.52V17.5204C3.5 17.7402 3.5 17.9301 3.50999 18.1C3.51999 18.28 3.55 18.47 3.63 18.67C3.81 19.1 4.15 19.44 4.58 19.62C4.78 19.7 4.96999 19.73 5.14999 19.74C5.31999 19.75 5.52 19.75 5.73 19.75C5.94 19.75 6.18001 19.75 6.35001 19.74C6.53001 19.73 6.72 19.7 6.92 19.62C7.35 19.44 7.69 19.1 7.87 18.67C7.95 18.47 7.98001 18.28 7.99001 18.1C8 17.9301 8 17.7402 8 17.5204V17.52V13.48V13.4796C8 13.2598 8 13.0699 7.99001 12.9C7.98001 12.72 7.95 12.53 7.87 12.33C7.69 11.9 7.35 11.56 6.92 11.38C6.72 11.3 6.53001 11.27 6.35001 11.26Z" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-3 h-3"><path fillRule="evenodd" clipRule="evenodd" fill="currentColor" d="M4 11.3125C3.45 11.3125 3 11.7625 3 12.3125C3 12.8625 3.45 13.3125 4 13.3125H7C7.55 13.3125 8 12.8625 8 12.3125C8 11.7625 7.55 11.3125 7 11.3125H4ZM9.5 12.3125C9.5 11.7625 9.95 11.3125 10.5 11.3125H13.5C14.05 11.3125 14.5 11.7625 14.5 12.3125C14.5 12.8625 14.05 13.3125 13.5 13.3125H10.5C9.95 13.3125 9.5 12.8625 9.5 12.3125ZM16 12.3125C16 11.7625 16.45 11.3125 17 11.3125H20C20.55 11.3125 21 11.7625 21 12.3125C21 12.8625 20.55 13.3125 20 13.3125H17C16.45 13.3125 16 12.8625 16 12.3125Z" /></svg>
                )}
                GPRS
              </span>
              )}
              {isAssetBeacon ? (
                <span className={`text-[10px] font-medium flex items-center gap-1 ${beaconFresh ? 'text-teal-600' : 'text-muted-foreground'}`} data-testid={tid('beacon-signal-ble')}>
                  <Bluetooth className="w-3 h-3" />
                  {beaconFresh
                    ? `BLE Observed${device.beacon_last_seen_by || device.beacon_live_state?.last_receiver_name ? ` · via ${device.beacon_last_seen_by || device.beacon_live_state?.last_receiver_name}` : ''}`
                    : 'BLE Not Detected'}
                </span>
              ) : (
              <span
                className={`text-[10px] font-medium flex items-center gap-0.5 ${gpsActive ? 'text-blue-600' : 'text-muted-foreground'}`}
                data-testid={tid('signal-gps-status')}
                title={(() => {
                  const fs = getGpsFixState(device);
                  if (!fs) return undefined;
                  const { fix } = fs;
                  const acc = fix.position_accuracy_m != null ? ` · Estimated accuracy: ${fix.position_accuracy_m} m` : '';
                  const lastFix = fix.gps_timestamp ? ` · Last fix: ${formatDT(fix.gps_timestamp, { timezone: effectiveTz })}` : '';
                  const base = fs.state === 'fresh'
                    ? `GPS fix obtained${fix.fix_3d ? ' · 3D' : ''}${acc}${lastFix}`
                    : fs.state === 'last_known'
                      ? `Last known GPS position · ${fix.gps_timestamp ? formatTimeAgo(fix.gps_timestamp) : 'age unknown'}${acc}${lastFix}`
                      : `No valid GPS fix reported${lastFix ? ` · Last known position retained${lastFix}` : ''}`;
                  return `${base} · Satellites: Not reported`;
                })()}
              >
                <SatelliteIcon className="w-3 h-3" />
                {(() => {
                  const fs = getGpsFixState(device);
                  if (!fs) {
                    if ((device.protocol || '') === 'Digital Matter') return 'GPS status unknown';
                    return `GPS ${gprsActive ? (device.last_satellites ?? device.last_position?.satellites ?? 0) : 0} sat`;
                  }
                  const { fix } = fs;
                  if (fs.state === 'fresh') return `GPS fix${fix.fix_3d ? ' · 3D' : ''}${fix.position_accuracy_m != null ? ` · ±${fix.position_accuracy_m}m` : ''}`;
                  if (fs.state === 'last_known') return `GPS last known · ${fix.gps_timestamp ? formatTimeAgo(fix.gps_timestamp) : 'age unknown'}`;
                  return 'No valid GPS fix';
                })()}
              </span>
              )}
            </div>
          </div>

          {/* Coordinates + Country Flag */}
          {coordsStr && (
            <div className="flex justify-between items-center py-1">
              <span className="text-xs text-muted-foreground">Coordinates</span>
              <div className="flex items-center gap-1">
                {countryCode && (
                  <span
                    className={`fi fi-${countryCode.toLowerCase()} !w-6 !h-[18px] rounded-[2px] border border-black/5`}
                    title={countryCode}
                    aria-label={countryCode}
                    data-testid={tid('country-flag')}
                  />
                )}
                <a
                  href={`https://www.google.com/maps?q=${device.last_latitude},${device.last_longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-[#2563eb] hover:underline font-mono"
                  data-testid={tid('coords-link')}
                >
                  {coordsStr}
                </a>
                <button
                  className="p-0.5 text-muted-foreground hover:text-foreground"
                  onClick={() => { navigator.clipboard.writeText(coordsStr); toast.success('Copied'); }}
                >
                  <Copy className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>
          )}

          {/* Address — fixed 3-line height to prevent layout shift on refresh */}
          <div className="flex justify-between items-start py-1 gap-1">
            <div className="flex flex-col gap-0.5 flex-shrink-0 mt-0.5">
              <span className="text-xs text-muted-foreground">Address</span>
              {addressSource && !addressLoading && (
                <span className={`text-[9px] px-1 py-0.5 rounded w-fit ${
                  addressSource === 'google'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                }`} data-testid={tid('address-source-badge')}>
                  {addressSource === 'google' ? 'Google' : 'OSM'}
                </span>
              )}
            </div>
            <div className="flex items-start gap-1">
              {addressLoading ? (
                <div className="h-[39px] flex items-center">
                  <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <span className="text-[10px] leading-[13px] text-right h-[39px] overflow-hidden line-clamp-3" data-testid={tid('telemetry-address')}>
                  {address || 'N/A'}
                </span>
              )}
              <button
                className="p-0.5 text-muted-foreground hover:text-foreground flex-shrink-0 mt-0.5"
                onClick={fetchAddress}
                title="Refresh address"
              >
                <RefreshCw className={`w-2.5 h-2.5 ${addressLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Street View — directly under address */}
          {hasPosition && (
            <div className="py-0.5">
              <a
                href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${device.last_latitude},${device.last_longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-2 py-1.5 rounded border border-border bg-muted/30 hover:bg-muted transition-colors text-xs text-muted-foreground hover:text-foreground"
                data-testid={tid('streetview-peek')}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M5 20c0-4 3.5-7 7-7s7 3 7 7" />
                  <path d="M12 12v3" />
                </svg>
                <span>Open Street View</span>
                <svg viewBox="0 0 24 24" className="w-3 h-3 ml-auto" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M17 7H8M17 7v9"/></svg>
              </a>
            </div>
          )}

          {/* Road Speed Limit — shown when available for any online device */}
          {roadSpeedLimit != null && device.status !== 'offline' && (
            <div className="py-1" data-testid={tid('telemetry-road-limit')}>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  Road Limit
                  {roadSpeedSource && (
                    <span className={`text-[9px] px-1 py-0.5 rounded ${
                      roadSpeedSource === 'osrm_estimated' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' :
                      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                    }`}>
                      {roadSpeedSource === 'osrm_estimated' ? 'Estimated' : 'OSM'}
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-1.5">
                  <SpeedLimitSign limit={Math.round(convertSpeed(roadSpeedLimit))} size={24} testIdPrefix={testIdPrefix} />
                  <span className="text-xs font-bold">{formatSpeed(roadSpeedLimit)}</span>
                </div>
              </div>
              {roadName && (
                <div className="text-[9px] text-muted-foreground text-right mt-0.5 truncate" data-testid={tid('telemetry-road-name')} title={roadName}>
                  {roadName}
                </div>
              )}
            </div>
          )}

          {!isAssetBeacon && (
            <>
              <InfoRow label={<span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> Speed</span>} value={gpsFresh ? formatSpeed(device.last_speed || 0) : `- ${speedUnitLabel()}`} className="font-bold" />
              <InfoRow
                label={<span className="flex items-center gap-1"><Compass className="w-3 h-3" /> Heading</span>}
                value={
                  gpsFresh
                    ? headingToCompass(device.last_heading)
                    : device.last_heading != null
                      ? <span className="text-muted-foreground">{headingToCompass(device.last_heading)} <span className="text-[9px] opacity-60">last</span></span>
                      : 'N/A'
                }
              />
            </>
          )}

          {/* Odometer — hidden when source is off */}
          {device.odometer_source !== 'off' && (
            <InfoRow
              label="Odometer"
              value={
                device.computed_odometer != null
                  ? formatDistance(device.computed_odometer, { decimals: 1 })
                  : device.odometer_reading != null
                  ? formatDistance(device.odometer_reading, { decimals: 1 })
                  : formatDistance(0, { decimals: 1 })
              }
            />
          )}

          {/* Engine Hours — hidden when source is off */}
          {device.engine_hours_source !== 'off' && (
            <InfoRow
              label="Engine Hours"
              value={
                device.computed_engine_hours != null
                  ? `${Number(device.computed_engine_hours).toFixed(1)} hrs`
                  : device.engine_hours != null
                  ? `${Number(device.engine_hours).toFixed(1)} hrs`
                  : '0.0 hrs'
              }
            />
          )}

          {/* Altitude — hidden when no position */}
          {hasPosition && device.last_altitude != null && (
            <InfoRow
              label="Altitude"
              value={
                gpsFresh
                  ? `${device.last_altitude} m`
                  : <span className="text-muted-foreground">{device.last_altitude} m <span className="text-[9px] opacity-60">last</span></span>
              }
            />
          )}
        </div>
        )}

        {/* ── LIVE OCCUPANCY — desktop: own section under the Overview, before Driver ── */}
        {!mobileSection && (
          <LiveOccupancySection bindings={bindings} tid={tid} isSubpageEnabled={isSubpageEnabled} navigate={navigate} effectiveTz={effectiveTz} offline={device.status === 'offline'} />
        )}

        {/* ── DATA SECTION — IO Mappings (collapsible on desktop, plain on mobile) ── */}
        {isSubpageEnabled('live_tracking_driver') && !isIgnitionOff && activeDriverSession?.person_name && (!mobileSection || mobileSection === 'driver') && (
          <SectionContainer mobile={!!mobileSection} title="Driver" icon={IdCard} testId={tid('telemetry-driver-section')} defaultOpen>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs dark:border-emerald-900 dark:bg-emerald-950/30" data-testid={tid('active-driver-card')}>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white">
                  <UserRound className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">CONFIRMED DRIVER</p>
                    <Badge variant="outline" className="h-5 border-emerald-300 bg-white/70 px-1.5 text-[9px] font-semibold text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200" data-testid={tid('active-driver-source-badge')}>
                      {driverSourceLabel(activeDriverSession)}
                    </Badge>
                  </div>
                  <p className="truncate text-sm font-bold text-emerald-950 dark:text-emerald-100" data-testid={tid('active-driver-name')}>
                    {activeDriverSession.person_id ? (
                      <button
                        type="button"
                        className="truncate underline-offset-2 hover:underline focus:outline-none"
                        onClick={() => navigate(peopleDeepLinkPath(activeDriverSession.person_type || 'driver', activeDriverSession.person_id, activeDriverSession.person_name))}
                        data-testid={tid('active-driver-name-link')}
                      >
                        {activeDriverSession.person_name}
                      </button>
                    ) : activeDriverSession.person_name}
                  </p>
                </div>
              </div>
              <div className="mt-2 space-y-1 border-t border-emerald-200/70 pt-2 dark:border-emerald-900/70">
                <InfoRow label="Started" value={formatTimestamp(activeDriverSession.started_at, effectiveTz)} testId={tid('active-driver-started')} />
              </div>
            </div>
          </SectionContainer>
        )}

        {/* Manual Driver Allocation — default driver shown when no live session covers the slot */}
        {isSubpageEnabled('live_tracking_driver') && !activeDriverSession?.person_name && bindings?.manual_assigned_driver && (!mobileSection || mobileSection === 'driver') && (
          <SectionContainer mobile={!!mobileSection} title="Driver" icon={IdCard} testId={tid('telemetry-manual-driver-section')} defaultOpen>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs dark:border-amber-900 dark:bg-amber-950/30" data-testid={tid('telemetry-manual-driver-row')}>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-amber-600 text-white">
                  <UserRound className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Assigned driver (manual)</p>
                  <p className="truncate text-sm font-bold text-amber-950 dark:text-amber-100" data-testid={tid('telemetry-manual-driver-name')}>
                    {bindings.manual_assigned_driver.person_id ? (
                      <button
                        type="button"
                        className="truncate underline-offset-2 hover:underline focus:outline-none"
                        onClick={() => navigate(peopleDeepLinkPath(bindings.manual_assigned_driver.person_type || 'driver', bindings.manual_assigned_driver.person_id, bindings.manual_assigned_driver.person_name))}
                        data-testid={tid('telemetry-manual-driver-name-link')}
                      >
                        {bindings.manual_assigned_driver.person_name}
                      </button>
                    ) : bindings.manual_assigned_driver.person_name}
                  </p>
                  <p className="mt-0.5 text-[10px] text-amber-700/80 dark:text-amber-300/80">A real RFID/BLE scan overrides this assignment.</p>
                </div>
              </div>
            </div>
          </SectionContainer>
        )}

        {/* Mobile webview: Live Occupancy lives inside the Occupants (driver) card */}
        {mobileSection === 'driver' && (
          <LiveOccupancySection
            bindings={bindings} tid={tid} isSubpageEnabled={isSubpageEnabled} navigate={navigate} effectiveTz={effectiveTz} offline={device.status === 'offline'} mobile
            showEmpty={!(isSubpageEnabled('live_tracking_driver') && !isIgnitionOff && activeDriverSession?.person_name)}
          />
        )}

        {isSubpageEnabled('live_tracking_telemetry_data') && telemetryMappings.length > 0 && (!mobileSection || mobileSection === 'data') && (
          <SectionContainer mobile={!!mobileSection} title="Telemetry Data" icon={Activity} testId={tid('telemetry-io-section')} defaultOpen>
            <div className={`space-y-0.5 ${!gprsActive ? 'opacity-40' : ''}`}>
              {!gprsActive && (
                <p className="text-xs text-amber-500 font-medium mb-1">Data may be stale — device is offline</p>
              )}
              {telemetryMappings.map((m) => {
                const displayVal = computeDisplayValue(m, flatIOElements);
                const colorClass = getValueColorClass(displayVal, m.value_colors);
                return (
                  <div key={m.id} className="flex justify-between items-center py-0.5" data-testid={tid(`io-row-${m.id}`)}>
                    <span className="text-xs text-muted-foreground truncate max-w-[50%]">{m.display_name}</span>
                    <span className={`text-xs font-semibold ${colorClass}`} data-testid={tid(`io-row-${m.id}-value`)}>{displayVal}</span>
                  </div>
                );
              })}
            </div>
          </SectionContainer>
        )}
        {mobileSection === 'data' && (!isSubpageEnabled('live_tracking_telemetry_data') || telemetryMappings.length === 0) && (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid={tid('telemetry-data-empty')}>No telemetry data configured</div>
        )}

        {/* ── COMMANDS SECTION (collapsible on desktop, plain on mobile) ── */}
        {isSubpageEnabled('live_tracking_commands') && telemetryCommands.length > 0 && (!mobileSection || mobileSection === 'commands') && (
          <SectionContainer mobile={!!mobileSection} title="Commands" icon={Send} testId={tid('telemetry-commands-section')} defaultOpen>
            <div className={mobileSection === 'commands' ? 'flex flex-col gap-3 pt-1 pb-3' : 'grid grid-cols-2 gap-1.5'}>
              {telemetryCommands.map((cmd) => (
                <Button
                  key={cmd.id}
                  size="sm"
                  className={`${mobileSection === 'commands' ? 'h-14 w-full text-base font-semibold rounded-lg' : 'text-[10px] h-7 px-2'} text-white ${CMD_BG[cmd.button_color] || CMD_BG.blue}`}
                  disabled={sendingCommand && sendConfirmCmd?.id === cmd.id}
                  onClick={(e) => { e.stopPropagation(); handleSendCommand(cmd); }}
                  data-testid={tid(`cmd-btn-${cmd.id}`)}
                  title={cmd.description || cmd.command_text}
                >
                  {sendingCommand && sendConfirmCmd?.id === cmd.id ? <Loader2 className={`${mobileSection === 'commands' ? 'w-5 h-5' : 'w-3 h-3'} animate-spin mr-2`} /> : <Send className={`${mobileSection === 'commands' ? 'w-5 h-5' : 'w-3 h-3'} mr-2`} />}
                  <span className="truncate">{cmd.button_label}</span>
                </Button>
              ))}
            </div>
          </SectionContainer>
        )}
        {mobileSection === 'commands' && (!isSubpageEnabled('live_tracking_commands') || telemetryCommands.length === 0) && (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid={tid('commands-empty')}>No commands configured</div>
        )}

        {/* ── EVENTS SECTION (collapsible on desktop, plain on mobile, shows Event Engine events) ── */}
        {isSubpageEnabled('live_tracking_events') && (!mobileSection || mobileSection === 'events') && (
          <SectionContainer mobile={!!mobileSection} title={`Events${deviceEvents.length > 0 ? ` (${deviceEvents.length})` : ''}`} icon={AlertTriangle} testId={tid('telemetry-events-section')} defaultOpen mobileClassName="pt-4">
            <p className={`${mobileSection ? 'text-[15px] mb-2.5' : 'text-[9px] mb-1.5'} font-semibold tracking-wide text-muted-foreground`}>Last 20 events up to 48 hours</p>
            {deviceEvents.length > 0 ? (
              <div className={mobileSection ? 'max-h-[calc(82dvh-10rem)] space-y-3 overflow-y-auto overscroll-contain pr-1' : 'space-y-1.5'} data-testid={mobileSection ? tid('telemetry-events-list-scroll') : undefined}>
                {deviceEvents.map((evt) => {
                const isSpeed = evt.event_type?.includes('overspeed');
                const sevColor = evt.severity === 'critical' ? 'text-red-600' : evt.severity === 'warning' ? 'text-amber-600' : 'text-slate-600';
                const isActive = evt.status === 'active';
                const sevBg = isActive
                  ? (evt.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800' : evt.severity === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800' : 'bg-slate-100 dark:bg-slate-800')
                  : 'bg-muted/30 border-border';
                return (
                  <div
                    key={evt.id}
                    className={`${mobileSection ? 'p-3.5 rounded-xl space-y-1.5' : 'p-2 rounded-md space-y-0.5'} border ${sevBg} ${onViewEventOnMap && evt.latitude != null && evt.longitude != null ? (mobileSection ? 'cursor-pointer active:border-blue-400 active:bg-blue-500/5 transition-colors' : 'cursor-pointer hover:ring-1 hover:ring-blue-400 transition-shadow') : ''}`}
                    onClick={() => {
                      if (onViewEventOnMap && evt.latitude != null && evt.longitude != null) {
                        try {
                          onViewEventOnMap(evt);
                        } finally {
                          if (mobileSection) onClose?.();
                        }
                      }
                    }}
                    data-testid={tid(`telemetry-event-${evt.id}`)}
                  >
                    <div className={`${mobileSection ? 'gap-2.5' : 'gap-1.5'} flex items-center min-w-0`}>
                      <Gauge className={`${mobileSection ? 'w-5 h-5' : 'w-3 h-3'} ${isActive ? sevColor : 'text-muted-foreground'} flex-shrink-0`} />
                      <span className={`${mobileSection ? 'text-[17px] leading-snug' : 'text-[11px]'} min-w-0 font-semibold truncate`}>{evt.event_rule_name}</span>
                      <Badge variant="outline" className={`${mobileSection ? 'text-[13px] px-2 py-0.5' : 'text-[9px] px-1 py-0'} shrink-0 capitalize ${isActive ? 'bg-red-100 text-red-600 border-red-200' : 'bg-green-100 text-green-600 border-green-200'}`}>{evt.status}</Badge>
                    </div>
                    {isSpeed && evt.speed_kmh != null && (
                      <p className={`${mobileSection ? 'text-[15px] pl-7' : 'text-[11px]'} font-medium ${isActive ? sevColor : 'text-muted-foreground'}`}>
                        {formatSpeed(evt.speed_kmh)} (Limit: {formatSpeed(evt.threshold_kmh)})
                      </p>
                    )}
                    {!isSpeed && evt.message && (
                      <p className={`${mobileSection ? 'text-[15px] leading-snug pl-7' : 'text-[11px]'} text-muted-foreground truncate`} data-testid={tid(`telemetry-event-message-${evt.id}`)}>
                        {evt.message}
                      </p>
                    )}
                    <p className={`${mobileSection ? 'text-[15px] leading-snug pl-7' : 'text-[10px]'} text-muted-foreground`}>
                      {evt.triggered_at ? formatTimestamp(evt.triggered_at, evt.device_timezone) : ''}
                      {evt.device_timezone ? ` — ${getTimezoneLabel(evt.device_timezone) || evt.device_timezone}` : ''}
                      {evt.duration_secs != null ? ` (${evt.duration_secs >= 60 ? `${Math.floor(evt.duration_secs/60)}m` : `${Math.round(evt.duration_secs)}s`})` : ''}
                    </p>
                  </div>
                );
              })}
            </div>
            ) : (
              <p className={`${mobileSection ? 'text-base py-3' : 'text-[11px] py-1'} text-muted-foreground`}>No active events</p>
            )}
          </SectionContainer>
        )}
        {mobileSection === 'events' && !isSubpageEnabled('live_tracking_events') && (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid={tid('events-empty')}>Events are not enabled</div>
        )}
      </div>

      {/* Command Confirmation Dialog — matches DeviceManagement style */}
      <Dialog open={!!sendConfirmCmd} onOpenChange={(open) => { if (!open) setSendConfirmCmd(null); }}>
        <DialogContent className="sm:max-w-md">
          {sendConfirmCmd && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
                    { blue: 'bg-blue-600', red: 'bg-red-600', green: 'bg-green-600', yellow: 'bg-yellow-500', orange: 'bg-orange-500', purple: 'bg-purple-600', gray: 'bg-gray-500', teal: 'bg-teal-600', pink: 'bg-pink-600', indigo: 'bg-indigo-600', cyan: 'bg-cyan-600', lime: 'bg-lime-600', rose: 'bg-rose-600' }[sendConfirmCmd.button_color] || 'bg-blue-600'
                  }`}>
                    <Send className="w-5 h-5" />
                  </div>
                  <div>
                    <DialogTitle>Send Command?</DialogTitle>
                    <DialogDescription>This will be sent to <span className="font-bold text-blue-600 dark:text-blue-400" data-testid={tid('cmd-confirm-device-name')}>{deviceDisplayName(device)}</span></DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-1 p-3 bg-muted rounded-lg">
                <p className="text-base font-semibold" data-testid={tid('cmd-confirm-button-name')}>{sendConfirmCmd.button_label}</p>
                {sendConfirmCmd.description && (
                  <p className="text-sm text-muted-foreground" data-testid={tid('cmd-confirm-description')}>{sendConfirmCmd.description}</p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" className={isMobileWebview ? 'h-12 text-base' : ''} onClick={() => setSendConfirmCmd(null)} disabled={sendingCommand}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={() => executeSendCommand(sendConfirmCmd)}
                  disabled={sendingCommand}
                  className={`${isMobileWebview ? 'h-12 text-base' : ''} text-white ${CMD_BG[sendConfirmCmd.button_color] || CMD_BG.blue}`}
                  data-testid={tid('cmd-confirm-send-btn')}
                >
                  {sendingCommand ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1" /> Send Command</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
